import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Confirm, Modal, Select } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table/Table';

import { Icons } from '@icons/icons';

import { useMutateDeleteSectorsSettings } from '@hooks/sectors/useMutateDeleteSectorsSettings';
import { useQueryAllSectors } from '@hooks/sectors/useQuerySectors';
import { useQuerySectorsByFilter } from '@hooks/sectors/useQuerySectorsByFilter';
import { useManageSitesOptions } from '@hooks/useManageSitesOptions';
import { useTechnologies } from '@hooks/useTechnologies';

import { auth, sectors } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setPage, setPageSize, setSort, setTechnology } from '@slices/sectors';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSort } from '@interfaces/components/table';
import { ISectorSettingDTO } from '@interfaces/map/dto/sectorsDTO';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EAdminPanelPageTitles, TTechnologiesValues, TVendorsValues } from '@enums/common';

import { MODAL_WIDTH_580 } from '@constants/modals';

import CreateSectorsSettingsForm from './SectorsSettingsForm/CreateSectorsSettingsForm';
import EditSectorSettingsForm from './SectorsSettingsForm/EditSectorSettingsForm';
import { getColumns } from './getColumns';

import styles from './SectorsSettings.module.scss';

const SectorsSettings = () => {
	const dispatch = useAppDispatch();
	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		technology,
	} = useAppSelector(sectors);
	const {
		accessPermission: {
			networkOperatorTelecomSitesViewer,
			networkOperatorTelecomSitesAttributeUpdater,
		},
	} = useAppSelector(auth);

	const [selectedVendor, setSelectedVendor] = useState<TSelectOption<TVendorsValues> | null>(null);
	const [cellSettingsIdForDelete, setCellSettingsIdForDelete] = useState<string | null>();
	const [editingItem, setEditItem] = useState<ISectorSettingDTO | null>(null);

	const [isCellSettingsFormOpen, setIsCellSettingsFormOpen] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	const isReadOnly = networkOperatorTelecomSitesViewer && !networkOperatorTelecomSitesAttributeUpdater;

	const { vendorsOptions } = useManageSitesOptions();
	const { sectorSets, isInitialLoading, count } = useQuerySectorsByFilter({
		vendor: selectedVendor?.value,
		technology: technology?.value,
	});

	const { allSectors } = useQueryAllSectors();

	useEffect(() => {
		if (!selectedVendor && vendorsOptions.length) {
			setSelectedVendor(vendorsOptions[0] as TSelectOption<TVendorsValues>);
		}
	}, [selectedVendor, vendorsOptions]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortFieldName: sortBy[0]?.id,
			sortDirection: sortBy[0]?.desc,
		}));
	}, [dispatch]);

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const pagination: ITablePagination = {
		countOfRecords: count,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	};

	const openDeleteDialog = useCallback(() => setIsDeleteDialogOpen(true), []);

	const onVendorChange = useCallback((option: TSelectOption) => {
		setSelectedVendor(option as TSelectOption<TVendorsValues>);
	}, []);

	const toggleForm = useCallback(() => setIsCellSettingsFormOpen(prevState => !prevState), []);

	const closeCellSettingsForm = useCallback(() => {
		setIsCellSettingsFormOpen(false);
		setEditItem(null);
	}, []);

	const noDataText = `No available cells for ${selectedVendor?.label} vendor`;

	const handleCloseDeleteDialog = useCallback(() => {
		setIsDeleteDialogOpen(false);
		setCellSettingsIdForDelete(undefined);
	}, []);

	const { deleteSectorsSettings, isDeletingSectorSettings } = useMutateDeleteSectorsSettings(handleCloseDeleteDialog);

	const handleDeleteSectorsSettings = useCallback(() => {
		if (!cellSettingsIdForDelete) return;

		deleteSectorsSettings(cellSettingsIdForDelete);
	}, [cellSettingsIdForDelete, deleteSectorsSettings]);

	const columns = useMemo(() => getColumns({
		setCellSettingsIdForDelete,
		openDeleteDialog,
		setEditItem,
		toggleForm,
		isReadOnly,
	}), [isReadOnly, openDeleteDialog, toggleForm]);

	const handleSetTech = useCallback((option: TSelectOptionType<TTechnologiesValues> | null) => {
		dispatch(setTechnology({ technology: option }));
	}, [dispatch]);

	const { technologiesOptions } = useTechnologies();

	const filters = useMemo(() => {
		return [
			{
				name: 'technology',
				placeholder: 'Select technology',
				option: technology,
				options: technologiesOptions,
				minWidth: '270px',
				setState: handleSetTech,
				defaultValue: null,
			},
		];
	}, [
		handleSetTech,
		technologiesOptions,
		technology,
	]);

	return (
		<div className={styles.container}>
			<PageTitle
				icon={Icons.manageSiteDBInverse()}
				title={EAdminPanelPageTitles.SectorsSettings}
			/>
			<div className={styles.whiteWrapper}>
				<div className={styles.selectWrapper}>
					<Select
						handleChange={onVendorChange as never}
						isWithoutUseForms
						label='Vendor'
						name='vendor'
						options={vendorsOptions}
						placeholder='Select vendor'
						value={selectedVendor}
						width='240px'
					/>
				</div>
				{selectedVendor && (
					<div className={styles.tableContainer}>
						<Table
							columns={columns}
							data={sectorSets}
							emptyText={noDataText}
							filters={filters}
							isCreatable={!isReadOnly}
							isEmptyTextCenter
							isLoading={isInitialLoading}
							isOpenFilters={!!technology}
							isWithoutCheckbox
							isWithoutDeleteAction
							isWithoutFilter={false}
							pagination={pagination}
							searchThreshold={0}
							sort={sort}
							toggleAddModal={toggleForm}
						/>
					</div>
				)}
			</div>
			{selectedVendor?.value && (
				<>
					<Modal
						isConfirmDialogNeededOnClose
						isDirtyForm={isDirtyFormModal}
						isOpen={isCellSettingsFormOpen}
						onClose={closeCellSettingsForm}
						title='Add new sectors settings'
						width={MODAL_WIDTH_580}
					>
						<CreateSectorsSettingsForm
							closeModal={closeCellSettingsForm}
							currentVendor={selectedVendor?.value}
							editingItem={null}
							existingSectorSettings={allSectors}
							setIsDirtyFormModal={setIsDirtyFormModal}
						/>
					</Modal>
					{editingItem && (
						<Modal
							isConfirmDialogNeededOnClose
							isDirtyForm={isDirtyFormModal}
							isOpen={!!editingItem}
							onClose={closeCellSettingsForm}
							title='Edit sectors settings'
							width={MODAL_WIDTH_580}
						>
							<EditSectorSettingsForm
								closeModal={closeCellSettingsForm}
								currentVendor={selectedVendor?.value}
								editingItem={editingItem}
								existingSectorSettings={allSectors}
								setIsDirtyFormModal={setIsDirtyFormModal}
							/>
						</Modal>
					)}
				</>
			)}

			<Modal
				isOpen={isDeleteDialogOpen}
				onClose={handleCloseDeleteDialog}
			>
				<Confirm
					isLoading={isDeletingSectorSettings}
					onSubmit={handleDeleteSectorsSettings}
					question='Are you sure you want to delete the Sectors settings?'
				/>
			</Modal>
		</div>
	);
};

export default SectorsSettings;
